import { AccessProfile } from '../models/auth.model';

export type BackofficeRole = 'root' | 'administrative' | 'teacher' | 'admission' | 'finance';

export const normalizeBackofficeRole = (
  role: string | number | null | undefined,
): BackofficeRole | null => {
  const normalized = normalizeRoleText(role);

  if (
    normalized === '4' ||
    normalized === 'root' ||
    normalized.includes('superuser') ||
    normalized.includes('super user') ||
    normalized.includes('super usuario') ||
    normalized.includes('superusuario')
  ) {
    return 'root';
  }

  if (
    normalized === '1' ||
    normalized === 'admin' ||
    normalized === 'administrative' ||
    normalized.includes('administrador') ||
    normalized.includes('administrativo') ||
    normalized.includes('administrativa') ||
    normalized.includes('administracion') ||
    normalized.includes('administration')
  ) {
    return 'administrative';
  }

  if (
    normalized === '3' ||
    normalized.includes('teacher') ||
    normalized.includes('professor') ||
    normalized.includes('profesor') ||
    normalized.includes('profesores') ||
    normalized.includes('docente') ||
    normalized.includes('maestro')
  ) {
    return 'teacher';
  }

  if (
    normalized === '5' ||
    normalized.includes('admission') ||
    normalized.includes('admision') ||
    normalized.includes('admisiones')
  ) {
    return 'admission';
  }

  if (
    normalized === '6' ||
    normalized.includes('finance') ||
    normalized.includes('finanzas') ||
    normalized.includes('financiero') ||
    normalized.includes('financiera')
  ) {
    return 'finance';
  }

  if (normalized === '2' || normalized.includes('student') || normalized.includes('alumno')) {
    return null;
  }

  return null;
};

export const canAccessBackoffice = (role: string | number | null | undefined): boolean =>
  normalizeBackofficeRole(role) !== null;

export const getDefaultBackofficeRoute = (role: string | number | null | undefined): string =>
  getDefaultBackofficeRouteForRoles([role]);

export const canManageBackoffice = (role: string | number | null | undefined): boolean =>
  canManageBackofficeRoles([role]);

/**
 * Priority order used whenever a user holds more than one area: the
 * highest-priority role decides the default landing route and the
 * management bypass. Order: root, administrative, admission, finance,
 * teacher.
 */
const BACKOFFICE_ROLE_PRIORITY: readonly BackofficeRole[] = [
  'root',
  'administrative',
  'admission',
  'finance',
  'teacher',
];

/**
 * Normalizes a list of raw role/area values into the known `BackofficeRole`
 * union, dropping unknown areas and nulls, deduplicating, and returning them
 * in the stable priority order above (never in caller-supplied order).
 */
export const normalizeBackofficeRoles = (
  roles: readonly (string | number | null | undefined)[] | null | undefined,
): BackofficeRole[] => {
  if (!roles || roles.length === 0) {
    return [];
  }

  const normalized = new Set(
    roles
      .map((role) => normalizeBackofficeRole(role))
      .filter((role): role is BackofficeRole => role !== null),
  );

  return BACKOFFICE_ROLE_PRIORITY.filter((role) => normalized.has(role));
};

export const canManageBackofficeRoles = (
  roles: readonly (string | number | null | undefined)[] | null | undefined,
): boolean => {
  const normalized = normalizeBackofficeRoles(roles);
  return normalized.includes('root') || normalized.includes('administrative');
};

export const getDefaultBackofficeRouteForRoles = (
  roles: readonly (string | number | null | undefined)[] | null | undefined,
): string => {
  const [highestPriorityRole] = normalizeBackofficeRoles(roles);

  if (highestPriorityRole === 'teacher') {
    return '/postgraduate/academic-load/groups';
  }

  if (highestPriorityRole === 'admission') {
    return '/postgraduate/services/applicants';
  }

  if (highestPriorityRole === 'finance') {
    return '/finanzas/ingresos/historial-pagos';
  }

  return '/dashboard';
};

/**
 * Behavior change from `canAccessBackoffice`: that function locks a user out
 * whenever their role name is not in the hardcoded `BackofficeRole` union,
 * which means a brand-new area created in the database (the whole point of
 * this migration) would block its members until the frontend redeploys.
 *
 * The new rule decides on the shape of the access profile instead of on
 * known role names:
 *   1. `userType === 'Student'` (case-insensitive) never reaches the
 *      backoffice, regardless of roles or permissions.
 *   2. Otherwise, holding at least one role OR at least one permission is
 *      enough to be let in — an unrecognized area name still grants access,
 *      it just won't resolve to a typed `BackofficeRole` for menu/route
 *      purposes.
 *   3. Otherwise (no profile data at all — e.g. the API call hasn't
 *      resolved yet, or failed) fall back to the legacy single-role check
 *      so unmigrated sessions keep working.
 */
export const canAccessBackofficeProfile = (
  profile: AccessProfile | null,
  legacyRole: string | null,
): boolean => {
  if (profile?.userType?.toLowerCase() === 'student') {
    return false;
  }

  if (profile && (profile.roles.length > 0 || profile.permissions.length > 0)) {
    return true;
  }

  return canAccessBackoffice(legacyRole);
};

const ROLE_CLAIM_KEYS = [
  'role',
  'roles',
  'userRole',
  'user_role',
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role',
];

export const extractBackofficeRoleFromToken = (
  token: string | null | undefined,
): BackofficeRole | null => {
  if (!token) return null;

  const parts = token.split('.');
  if (parts.length < 2) return null;

  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    const decoded = JSON.parse(atob(paddedPayload)) as Record<string, unknown>;

    for (const key of ROLE_CLAIM_KEYS) {
      const role = normalizeBackofficeRoleClaim(decoded[key]);

      if (role) {
        return role;
      }
    }

    return null;
  } catch {
    return null;
  }
};

const normalizeBackofficeRoleClaim = (value: unknown): BackofficeRole | null => {
  if (Array.isArray(value)) {
    for (const item of value) {
      const role = normalizeBackofficeRoleClaim(item);

      if (role) {
        return role;
      }
    }

    return null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return normalizeBackofficeRole(value);
  }

  return null;
};

const normalizeRoleText = (role: string | number | null | undefined): string =>
  String(role ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
